/**
 * Guards the Snowplow collector endpoint against non-production environments.
 *
 * `REACT_APP_TRACKING_HOST` is spread over the `IS_PROD` feature default in `createConfig`, so a
 * developer whose `.env` still points at the production collector sends real events from localhost.
 * This module makes the environment — not the `.env` file — the authority on which collector is used.
 */

import { NON_PRODUCTION_TRACKING_HOST, PRODUCTION_TRACKING_HOST } from 'constants/snowplow'

/**
 * Hostnames that identify a developer machine rather than a deployed environment.
 * IPv6 loopback is listed only in its bracketed form: `new URL('https://::1')` throws, so a bare
 * `::1` normalises to an empty string and could never match.
 */
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0', '[::1]'])

/**
 * Normalises a configured tracking host for comparison against the known collectors, tolerating the
 * missing schemes, trailing slashes and casing that appear in hand-edited `.env` files.
 *
 * @returns The bare lowercase hostname, or an empty string when one cannot be derived.
 */
function extractHostname(host: string): string {
  const trimmed = host.trim()
  if (!trimmed) return ''

  // `.env` values are frequently written without a scheme (e.g. `t.luxuryescapes.com`), which URL
  // cannot parse on its own, so add a placeholder scheme before parsing.
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`

  try {
    return new URL(withScheme).hostname.toLowerCase()
  } catch {
    return ''
  }
}

/** True when events sent to this host would reach production analytics. */
function isProductionTrackingHost(host: string | undefined): boolean {
  if (!host) return false
  return extractHostname(host) === extractHostname(PRODUCTION_TRACKING_HOST)
}

/** True when the host is a loopback address, i.e. a developer's machine. Accepts an optional port. */
function isLocalHost(host: string | undefined): boolean {
  if (!host) return false
  return LOCAL_HOSTNAMES.has(extractHostname(host))
}

interface ResolveTrackingHostOptions {
  /** The configured collector, i.e. `REACT_APP_TRACKING_HOST` or the brand feature default. */
  trackingHost: string | undefined
  /** The environment the app believes it is running in. */
  appEnv: string | undefined
  /**
   * The host the app is being served from (`REACT_APP_CUSTOMER_PORTAL_HOST` on the server,
   * `window.location.hostname` in the browser). Catches a developer who sets `APP_ENV=production`
   * locally, which an `appEnv` check alone would wave through.
   */
  servingHost?: string
  /** Injected for testability; defaults to the console. */
  warn?: (message: string) => void
}

/**
 * Resolves the Snowplow collector the app is permitted to use, downgrading the production collector
 * to staging whenever the app is not genuinely running in production.
 *
 * Non-production hosts (staging, Snowplow Micro, an empty value) are returned untouched — this only
 * ever takes the production collector away, it never introduces one.
 *
 * @returns The collector to use. Identical to `trackingHost` unless a downgrade was required.
 */
export function resolveTrackingHost(options: ResolveTrackingHostOptions & { trackingHost: string }): string
export function resolveTrackingHost(options: ResolveTrackingHostOptions): string | undefined
export function resolveTrackingHost({
  trackingHost,
  appEnv,
  servingHost,
  // eslint-disable-next-line no-console
  warn = message => console.warn(message),
}: ResolveTrackingHostOptions): string | undefined {
  if (!isProductionTrackingHost(trackingHost)) return trackingHost

  const isProductionEnvironment = appEnv === 'production'
  const isServedLocally = isLocalHost(servingHost)

  if (isProductionEnvironment && !isServedLocally) return trackingHost

  const reason = isServedLocally ?
    `it is being served from "${servingHost}"` :
    `APP_ENV is "${appEnv ?? 'undefined'}"`

  warn(
    `[analytics] Refusing to send tracking data to the production Snowplow collector because ${reason}. ` +
    `Falling back to ${NON_PRODUCTION_TRACKING_HOST}. ` +
    `Set REACT_APP_TRACKING_HOST=${NON_PRODUCTION_TRACKING_HOST} in your .env to silence this warning.`,
  )

  return NON_PRODUCTION_TRACKING_HOST
}
